import type { SagaIterator } from 'redux-saga';
import { call, delay, fork, put, select, takeLatest } from 'redux-saga/effects';
import { SAGA_ACTIONS } from '../sagas/sagaActions';
import {
  handleCustomerInfo,
  watchSetBillingForm,
} from '../shared/billing/billingSagas';
import {
  changeCart,
  deleteCart,
  getCart,
  getCatalogData,
  getCountries,
  getCustomerInfo,
  getLocalization,
  getPreview,
  getProductCmsData,
  getSession,
  removeItem,
  setItemQuantity,
  swapItem,
} from '../shared/requestSagas';
import {
  setCountries,
  setEligibility,
  setError,
  setLocalization,
  setReadonly,
  setSession,
} from '../store/appSlice';
import {
  clearForm,
  clearInfo,
  setForm as setBillingForm,
} from '../store/billingSlice';
import {
  type Catalog,
  setCatalog,
  setInitialized,
  setLoading,
  setPersistentCart,
  setProductsCmsData,
  setProductsPreview,
  setPromoCode,
} from '../store/cartSlice';
import { resetEdu, setForm as setEduStatusFrom } from '../store/eduSlice';
import { selectRouteQuery } from '../store/routingSelectors';
import type { Cart, CartPatch, CartProduct } from '../types/cart';
import type { CatalogItem } from '../types/catalogItem';
import type { InvoiceItem } from '../types/preview';
import type { ProductCmsData } from '../types/productCms';
import { ApiError } from '../utils/api';
import { onAddToCart, onInit, onLogin, onRemoveProduct } from '../utils/gtm';
import { isTrial } from '../utils/products';
import { getCookie, isCloud, isEdu } from '../utils/utils';
import { handleValidateEmail } from '../utils/validators/validators';
import { watchEduForm, watchSubmitProofOfStatus } from './edu/sagas';
import { watchPayment } from './payment/paymentSagas';

export function* navigateCart(): SagaIterator<void> {
  yield fork(watchQuantityChange);
  yield fork(watchSwapProduct);
  yield fork(watchRemoveProduct);
  yield fork(watchApplyPromoCode);

  const { initialized } = yield select((state) => state.cart);

  if (!initialized) {
    yield call(init);
    yield call(applyPromoCodeFromQuery);
    return;
  }

  yield call(applyPromoCodeFromQuery);

  const { localization } = yield select((state) => state.app);
  const { catalog, productsPreview } = yield select((state) => state.cart);

  if (productsPreview) {
    onInit({ catalog, productsPreview, localization });
  }
}

export function* navigateCheckout() {
  yield fork(watchLoginSuccess);
  yield fork(watchQuantityChange);
  yield fork(watchSwapProduct);
  yield fork(watchRemoveProduct);
  yield fork(watchApplyPromoCode);
  yield fork(watchCheckEmail);
  yield fork(watchUpdatePersistentCart);
  yield fork(watchSetBillingForm);
  yield fork(watchCountryChange);
  yield fork(watchEduForm);
  yield fork(watchSubmitProofOfStatus);
  yield fork(watchPayment);

  const { initialized } = yield select((state) => state.cart);

  if (!initialized) {
    yield call(init);
  }

  yield call(applyPromoCodeFromQuery);
}

function* watchLoginSuccess() {
  yield takeLatest(SAGA_ACTIONS.LOGIN_SUCCESS, handleLoginSuccess);
}

function* handleLoginSuccess(): SagaIterator<void> {
  yield call(init);

  const { session, localization } = yield select((state) => state.app);
  const { catalog, productsPreview } = yield select((state) => state.cart);
  const { info } = yield select((state) => state.billing);

  if (productsPreview) {
    onLogin({
      catalog,
      productsPreview,
      localization,
      session,
      email: session?.Email,
      billingInfo: info,
    });
  }
}

export function* init(): SagaIterator<void> {
  yield put(setLoading(true));

  const session = yield call(getSession);
  yield put(setSession(session));

  yield put(clearForm());
  yield put(clearInfo());
  yield put(setEligibility(null));
  yield put(resetEdu());

  yield put(setBillingForm({ country: session.Location }));
  yield put(setEduStatusFrom({ institutionCountry: session.Location }));

  const countries = yield call(getCountries);
  yield put(setCountries(countries));

  const localization = yield call(getLocalization);
  yield put(setLocalization(localization));

  if (session.Email) {
    const customerInfo = yield call(getCustomerInfo);
    yield call(handleCustomerInfo, customerInfo);
  }

  // A shared cart link (e.g. an offer from an account manager) carries the
  // cart's UUID in ?cart=, like in the webshop.
  const query = yield select(selectRouteQuery);
  const cart = yield call(getCart, query?.cart);
  const promoCode = cart.promoCode || '';
  yield put(setPersistentCart(cart));
  // TODO review if both are needed
  yield put(setPromoCode(promoCode));
  yield put(setReadonly(cart.readOnly));

  yield call(updateCartData, cart);

  const { initialized, catalog, productsPreview } = yield select(
    (state) => state.cart,
  );

  if (productsPreview && !initialized) {
    onInit({ catalog, productsPreview, localization });
  }

  yield put(setInitialized(true));
  yield put(setLoading(false));
}

function* watchCheckEmail() {
  yield takeLatest(SAGA_ACTIONS.CHECK_EMAIL, handleValidateEmail);
}

function* watchQuantityChange() {
  yield takeLatest(SAGA_ACTIONS.CHANGE_QUANTITY, quantityChange);
}

function* quantityChange(action: {
  type: string;
  id: string;
  qty: number;
}): SagaIterator<void> {
  const { id, qty } = action;
  yield delay(750);

  const { localization } = yield select((state) => state.app);
  const { catalog, productsPreview } = yield select((state) => state.cart);
  const oldQty =
    productsPreview?.invoiceItems.find(
      (item: InvoiceItem) => item.productRatePlanId === id,
    )?.quantity || 0;

  yield put(setLoading(true));

  yield call(setItemQuantity, id, qty);

  const cart = yield call(getCart);
  yield put(setPersistentCart(cart));

  yield call(updateCartData, cart);

  const delta = Number(qty) - oldQty;
  if (delta > 0) {
    onAddToCart({
      catalog,
      localization,
      ratePlan: id,
      quantity: delta,
      placement: 'shoping_cart',
    });
  } else if (delta < 0) {
    onRemoveProduct({
      catalog,
      localization,
      ratePlan: id,
      quantity: Math.abs(delta),
      placement: 'shoping_cart',
    });
  }

  yield put(setLoading(false));
}

function* watchSwapProduct() {
  yield takeLatest(SAGA_ACTIONS.SWAP_PRODUCT, swapProductSaga);
}

function* swapProductSaga(action: {
  type: string;
  ratePlan: string;
  quantity: number;
  swapWithRatePlan: string;
}): SagaIterator<void> {
  const { ratePlan, quantity, swapWithRatePlan } = action;
  yield put(setLoading(true));

  const { localization } = yield select((state) => state.app);
  const { catalog } = yield select((state) => state.cart);

  yield call(swapItem, { ratePlan, quantity }, { ratePlan: swapWithRatePlan });

  const cart = yield call(getCart);
  yield put(setPersistentCart(cart));

  yield call(updateCartData, cart);

  onRemoveProduct({
    catalog,
    localization,
    ratePlan,
    quantity,
    placement: 'shoping_cart',
  });

  const { catalog: updatedCatalog } = yield select((state) => state.cart);
  onAddToCart({
    catalog: updatedCatalog,
    localization,
    ratePlan: swapWithRatePlan,
    quantity,
    placement: 'shoping_cart',
  });

  yield put(setLoading(false));
}

function* watchRemoveProduct() {
  yield takeLatest(SAGA_ACTIONS.REMOVE_PRODUCT, removeProductSaga);
}

function* removeProductSaga(action: {
  type: string;
  ratePlan: string;
}): SagaIterator<void> {
  yield put(setLoading(true));

  const { localization } = yield select((state) => state.app);
  const { catalog, productsPreview } = yield select((state) => state.cart);
  const quantity =
    productsPreview?.invoiceItems.find(
      (item: InvoiceItem) => item.productRatePlanId === action.ratePlan,
    )?.quantity || 0;

  const { readOnly } = yield select((state) => state.app);
  // A read-only cart refuses single removals, so delete it whole, like the
  // webshop does.
  if (readOnly) {
    yield call(deleteCart);
  } else {
    yield call(removeItem, action.ratePlan);
  }

  const cart = yield call(getCart);
  yield put(setPersistentCart(cart));
  yield put(setReadonly(cart.readOnly));

  yield call(updateCartData, cart);

  onRemoveProduct({
    catalog,
    localization,
    ratePlan: action.ratePlan,
    quantity,
    placement: 'chaos_site',
  });

  yield put(setLoading(false));
}

function* watchUpdatePersistentCart() {
  yield takeLatest(
    SAGA_ACTIONS.UPDATE_PERSISTENT_CART,
    updatePersistentCartSaga,
  );
}

function* updatePersistentCartSaga(action: {
  type: string;
  data: CartPatch;
}): SagaIterator<void> {
  yield put(setLoading(true));
  const { data } = action;

  yield call(changeCart, data);

  const cart = yield call(getCart);
  yield put(setPersistentCart(cart));

  yield put(setLoading(false));
}

function* watchApplyPromoCode() {
  yield takeLatest(SAGA_ACTIONS.APPLY_PROMO_CODE, applyPromoCodeSaga);
}

function* applyPromoCodeSaga(action: {
  type: string;
  promoCode: string;
}): SagaIterator<void> {
  yield call(applyPromo, action.promoCode);
}

function* applyPromo(promoCode: string): SagaIterator<void> {
  yield put(setLoading(true));

  yield call(changeCart, { promoCode });

  const cart = yield call(getCart);
  yield put(setPersistentCart(cart));

  yield call(updateCartData, cart);

  yield put(setLoading(false));
}

function* applyPromoCodeFromQuery(): SagaIterator<void> {
  const query = yield select(selectRouteQuery);
  const queryCode = query?.promoCode?.trim();

  if (!queryCode) {
    return;
  }

  const { persistentCart } = yield select((state) => state.cart);

  if (queryCode === persistentCart?.promoCode) {
    return;
  }

  yield put(setPromoCode(queryCode));
  yield call(applyPromo, queryCode);
}

function* watchCountryChange() {
  yield takeLatest(SAGA_ACTIONS.CHANGE_COUNTRY, countryChange);
}

function* countryChange(action: {
  type: string;
  country: string;
}): SagaIterator<void> {
  yield put(setBillingForm({ country: action.country }));

  const { persistentCart } = yield select((state) => state.cart);
  if (!persistentCart || persistentCart.products.length === 0) {
    return;
  }

  yield put(setLoading(true));
  yield call(updateCartData, persistentCart);
  yield put(setLoading(false));
}

function* updateCartData(cart: Cart) {
  if (cart.products.length === 0) {
    yield put(setCatalog(null));
    yield put(setProductsPreview(null));
    return;
  }

  const availableCart: Cart = yield call(handleCatalogInfo, cart);

  if (availableCart.products.length === 0) {
    yield put(setCatalog(null));
    yield put(setProductsPreview(null));
    return;
  }

  const currentCart: Cart = yield call(enforceMaxAmt, availableCart);

  yield call(handleProductPreviewData, currentCart.products);
}

function* enforceMaxAmt(cart: Cart): SagaIterator<Cart> {
  if (cart.readOnly) {
    return cart;
  }

  const catalog: Catalog | null = yield select((state) => state.cart.catalog);

  const productsOverMaxAmt: { ratePlan: string; maxAmt: number }[] = [];

  for (const product of cart.products) {
    const maxAmt = catalog?.items.find(
      (catalogItem) => catalogItem.ratePlanId === product.ratePlan,
    )?.maxAmt;
    if (maxAmt && product.quantity > maxAmt) {
      productsOverMaxAmt.push({ ratePlan: product.ratePlan, maxAmt });
    }
  }

  if (productsOverMaxAmt.length === 0) {
    return cart;
  }

  for (const product of productsOverMaxAmt) {
    yield call(setItemQuantity, product.ratePlan, product.maxAmt);
  }

  const cartWithCappedQuantities: Cart = yield call(getCart);
  yield put(setPersistentCart(cartWithCappedQuantities));

  return cartWithCappedQuantities;
}

function* handleCatalogInfo(persistentCart: Cart): SagaIterator<Cart> {
  const cartRatePlans: string[] = persistentCart.products.map(
    (product: CartProduct) => product.ratePlan,
  );
  const catalogItems: CatalogItem[] = yield call(getCatalogData, cartRatePlans);
  // Before the preview, so a trial dropped for a missing description is never
  // priced into it.
  const catalogProductsInfo: CatalogItem[] = yield call(
    handleProductsCmsData,
    catalogItems,
    persistentCart.products,
  );

  const products = persistentCart.products.filter((product: CartProduct) =>
    catalogProductsInfo.some(
      (productInfo: CatalogItem) => productInfo.ratePlanId === product.ratePlan,
    ),
  );

  if (products.length > 0) {
    const hasEduProduct = catalogProductsInfo.some((productInfo: CatalogItem) =>
      isEdu(productInfo.productCode),
    );
    const hasCloudCredits = catalogProductsInfo.some(
      (productInfo: CatalogItem) => isCloud(productInfo.productCode),
    );
    yield put(
      setCatalog({
        items: catalogProductsInfo,
        hasEduProduct,
        hasCloudCredits,
      }),
    );
  }

  if (products.length === persistentCart.products.length) {
    return persistentCart;
  }

  const cart: Cart = { ...persistentCart, products };
  yield put(setPersistentCart(cart));

  return cart;
}

function* handleProductPreviewData(
  cartItems: CartProduct[],
): SagaIterator<void> {
  const {
    app: { localization },
    cart,
    billing,
  } = yield select((state) => state);
  const { form = '' } = billing || {};

  const ratePlans = cartItems.map((item) => ({
    ratePlanId: item.ratePlan,
    seats: Number(item.quantity),
  }));

  const referralUcc = getCookie('referral_ucc');

  try {
    const data = {
      promoCode: cart.promoCode || '',
      account: {
        city: form.city || '',
        country: form.country || localization.countryCode,
        zipCode: form.zip || '',
        state: '',
        vatId: form.vat,
        currency: localization.currencyCode,
      },
      products: ratePlans,
      ...(referralUcc && { referralUcc }),
    };

    const previewData = yield call(getPreview, data);
    if (!previewData?.invoiceItems) {
      yield put(setError(previewData?.message || ''));
      yield call(resetCart);
      yield call(deleteCart);
      return;
    }

    // backend returns invoiceItems in a random order
    const ids = cartItems.map((item) => item.ratePlan);
    const sortedInvoiceItems = previewData.invoiceItems.sort(
      (a: InvoiceItem, b: InvoiceItem) =>
        ids.indexOf(a.productRatePlanId) - ids.indexOf(b.productRatePlanId),
    );
    const productPreviewData = {
      ...previewData,
      invoiceItems: sortedInvoiceItems,
    };

    const promoItems = productPreviewData.invoiceItems.filter(
      (item: InvoiceItem) => item.productName === 'Promotion',
    );
    const invoiceItemsNoPromo = productPreviewData.invoiceItems.filter(
      (item: InvoiceItem) => item.productName !== 'Promotion',
    );

    yield put(
      setProductsPreview({
        ...productPreviewData,
        invoiceItems: invoiceItemsNoPromo,
        promoItems: {
          list: promoItems,
          promoCode: cart.promoCode,
        },
      }),
    );
  } catch (error) {
    yield put(setError('', error, { cartItems }));
    yield call(resetCart);
  }
}

/** Returns `catalogItems` without the trials whose description is missing. */
function* handleProductsCmsData(
  catalogItems: CatalogItem[],
  cartProducts: CartProduct[],
): SagaIterator<CatalogItem[]> {
  const existing: ProductCmsData[] | null = yield select(
    (state) => state.cart.productsCmsData,
  );
  const missing = catalogItems.filter(
    (item) => !existing?.some((cms) => cms?.ratePlanId === item.ratePlanId),
  );
  if (missing.length === 0) {
    return catalogItems;
  }

  const productsCmsData: ProductCmsData[] = [];
  const describedItems: CatalogItem[] = [];
  for (const item of catalogItems) {
    const cached = existing?.find((cms) => cms?.ratePlanId === item.ratePlanId);
    try {
      const data =
        cached ??
        (yield call(getProductCmsData, item.ratePlanId, isTrial(item)));
      if (data) {
        productsCmsData.push(data);
      }
      describedItems.push(item);
    } catch (error) {
      // Only a trial throws, and a 404 means it has no description: drop it
      // silently. Other failures keep the item, as for any other product.
      if (error instanceof ApiError && error.code === 404) {
        yield call(
          removeItem,
          item.ratePlanId,
          cartProducts.find((product) => product.ratePlan === item.ratePlanId)
            ?.sourceRatePlan,
        );
      } else {
        describedItems.push(item);
      }
    }
  }

  yield put(setProductsCmsData(productsCmsData));

  return describedItems;
}

export function* resetCart() {
  yield put(setCatalog(null));
  yield put(setPersistentCart(null));
  yield put(setProductsPreview(null));
}
